import axios from 'axios';
import { LoginResponse, OTPVerifyResponse, OTPResendResponse } from '../types/user.ts';
import { FuelStation, FuelPriceReport, FuelAnalytics } from '../types/fuel.ts';

// Use environment variable for API URL in production, fallback to relative path for dev proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for 401/403 handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — clear auth and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('session');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Permission denied — let the caller handle it, but log it
      console.warn('Permission denied:', error.response?.data?.detail || 'Insufficient permissions');
    }
    return Promise.reject(error);
  }
);

// ItemKits
export const fetchItemKits = async (offset: number = 0, limit: number = 12, countryId?: number) => {
  let url = `/itemkits/list?offset=${offset}&limit=${limit}`;
  if (countryId !== undefined) {
    url += `&country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchItemKitById = async (id: string) => {
  const response = await apiClient.get(`/itemkits/${id}`);
  return response.data;
};

export const updateItemKit = async (id: string, itemKitData: { name: string; avater: string; lowest_price: number; is_feature: boolean; enable: boolean }) => {
  const response = await apiClient.put(`/itemkits/update/${id}`, itemKitData);
  return response.data;
};

export const createItemKit = async (itemKitData: { name: string; avater: string; lowest_price: number; is_feature: boolean; enable: boolean; country_id: number }) => {
  const response = await apiClient.post('/itemkits', itemKitData);
  return response.data;
};

export const fetchItemKitsCount = async (countryId?: number) => {
  let url = '/itemkits/count';
  if (countryId !== undefined) {
    url += `?country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  // Use nullish coalescing to handle count of 0 properly
  const count = response.data.count ?? response.data.total ?? response.data;
  return typeof count === 'number' ? count : 0;
};

// Items
export const fetchItemsCount = async () => {
  const response = await apiClient.get('/items/total/count');
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchItems = async (offset: number, count: number) => {
  const response = await apiClient.get(`/items/from/${offset}/count/${count}`);
  return response.data;
};

export const fetchItemById = async (id: string) => {
  const response = await apiClient.get(`/items/${id}`);
  return response.data;
};

export const updateItem = async (id: string, itemData: { name: string; avater: string; price: number; regular_price: number; url: string; enable: boolean }) => {
  const response = await apiClient.put(`/items/update/${id}`, itemData);
  return response.data;
};

export const fetchItemsByItemKit = async (itemKitId: number) => {
  const response = await apiClient.get(`/items/byitemkit/${itemKitId}`);
  return response.data;
};

export const createItem = async (itemData: { 
  id_itemkits: number; 
  id_retailers: number; 
  name: string; 
  avater: string; 
  price: number; 
  url: string; 
  isbn: string; 
  enable: boolean;
  country_id: number;
}) => {
  const response = await apiClient.post('/items', itemData);
  return response.data;
};

export const deleteItem = async (id: number) => {
  const response = await apiClient.delete(`/items/${id}`);
  return response.data;
};

// Users
export const fetchUsersCount = async (countryId?: number, stateId?: number, testAccount?: boolean) => {
  const params: Record<string, number | boolean> = {};
  if (countryId !== undefined) params.country_id = countryId;
  if (stateId !== undefined) params.state_id = stateId;
  if (testAccount !== undefined) params.test_account = testAccount;
  const response = await apiClient.get('/user/count', { params });
  // Handle count being 0 (falsy) - use nullish coalescing instead of ||
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchUsers = async (offset: number, count: number, countryId?: number, stateId?: number, testAccount?: boolean) => {
  const params: Record<string, number | boolean> = {};
  if (countryId !== undefined) params.country_id = countryId;
  if (stateId !== undefined) params.state_id = stateId;
  if (testAccount !== undefined) params.test_account = testAccount;
  const response = await apiClient.get(`/users/from/${offset}/count/${count}`, { params });
  return response.data;
};

export const fetchUserById = async (id: string) => {
  const response = await apiClient.get(`/user/info/${id}`);
  return response.data;
};

export const updateUser = async (id: string, userData: { first_name: string; middle_name: string; last_name: string; email: string; phone: string; postcode: number; state: string; country: string; country_id: number; state_id: number; device_name: string; is_active: boolean; ads: boolean; can_order: boolean; test_account: boolean }) => {
  const response = await apiClient.put(`/user/update/${id}`, userData);
  return response.data;
};

// Auth
export const login = async (usernameOrEmail: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post('/superuser/login', {
      username_or_email: usernameOrEmail,
      password: password
    });
    
    console.log('Raw API response:', response);
    console.log('Response data:', response.data);
    
    return response.data;
  } catch (error: any) {
    console.error('Login API error:', error);
    if (error.response) {
      // Server responded with error
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      throw error;
    } else if (error.request) {
      // Request made but no response
      console.error('No response received:', error.request);
      throw new Error('No response from server. Please check your connection and ensure the backend server is running.');
    } else {
      // Something else happened
      console.error('Error setting up request:', error.message);
      throw new Error(error.message || 'Login failed');
    }
  }
};

// Fetch current user info (with fresh permissions from DB)
export const fetchCurrentUser = async () => {
  const response = await apiClient.get('/superuser/me');
  return response.data;
};

// OTP Verification
export const verifyAdminOTP = async (superuserId: number, code: string): Promise<OTPVerifyResponse> => {
  try {
    console.log('Verifying OTP for superuser ID:', superuserId);
    const response = await apiClient.post('/superuser/verify-otp', {
      superuser_id: superuserId,
      code: code
    });
    return response.data;
  } catch (error: any) {
    console.error('OTP verification error:', error);
    throw error;
  }
};

export const resendAdminOTP = async (superuserId: number): Promise<OTPResendResponse> => {
  try {
    console.log('Resending OTP for superuser ID:', superuserId);
    const response = await apiClient.post('/superuser/resend-otp', {
      superuser_id: superuserId
    });
    return response.data;
  } catch (error: any) {
    console.error('OTP resend error:', error);
    throw error;
  }
};

// Countries
export const fetchCountries = async (offset: number, count: number) => {
  const response = await apiClient.get('/location/countries/list', { params: { offset, limit: count } });
  return response.data;
};

export const fetchCountriesCount = async () => {
  const response = await apiClient.get('/location/countries/count');
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchCountryById = async (id: string) => {
  const response = await apiClient.get(`/location/countries/${id}`);
  return response.data;
};

export const updateCountry = async (id: string, countryData: { name: string; code: string; enable: boolean }) => {
  const response = await apiClient.put(`/location/countries/${id}`, countryData);
  return response.data;
};

export const createCountry = async (countryData: { name: string; code: string; enable: boolean }) => {
  const response = await apiClient.post('/location/countries', countryData);
  return response.data;
};

export const deleteCountry = async (id: string) => {
  const response = await apiClient.delete(`/location/countries/${id}`);
  return response.data;
};

// Fetch all active countries for dropdowns
export const fetchCountriesForDropdown = async () => {
  const response = await apiClient.get('/location/countries');
  return response.data;
};

// States
export const fetchStates = async (offset: number, count: number, countryId?: number) => {
  const params: any = { offset, limit: count };
  if (countryId !== undefined) {
    params.country_id = countryId;
  }
  const response = await apiClient.get('/location/states/list', { params });
  return response.data;
};

export const fetchStatesByCountry = async (countryId: number) => {
  const response = await apiClient.get(`/location/states/bycountry/${countryId}`);
  return response.data;
};

export const fetchStatesCount = async (countryId?: number) => {
  let url = '/location/states/count';
  if (countryId !== undefined) {
    url += `?country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  // Handle count being 0 (falsy) - use nullish coalescing
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchStateById = async (id: string) => {
  const response = await apiClient.get(`/location/states/${id}`);
  return response.data;
};

export const updateState = async (id: string, stateData: { name: string; code: string; country_id: number; enable: boolean }) => {
  const response = await apiClient.put(`/location/states/${id}`, stateData);
  return response.data;
};

export const createState = async (stateData: { name: string; code: string; country_id: number; enable: boolean }) => {
  const response = await apiClient.post('/location/states', stateData);
  return response.data;
};

export const deleteState = async (id: string) => {
  const response = await apiClient.delete(`/location/states/${id}`);
  return response.data;
};

// Retailers
export const fetchRetailers = async (offset: number, count: number, countryId?: number) => {
  let url = `/retailer/from/${offset}/count/${count}`;
  if (countryId !== undefined) {
    url += `?country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchRetailersForDropdown = async () => {
  // Fetch a large number to get all retailers for dropdown
  const response = await apiClient.get(`/retailer/from/0/count/1000`);
  return response.data;
};

export const fetchRetailersByCountry = async (countryId: number) => {
  const response = await apiClient.get(`/retailers/bycountry/${countryId}`);
  return response.data;
};

export const fetchRetailersCount = async (countryId?: number) => {
  let url = '/retailer/total/count';
  if (countryId !== undefined) {
    url += `?country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  // Handle count being 0 (falsy) - use nullish coalescing
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchRetailerById = async (id: string) => {
  const response = await apiClient.get(`/retailer/${id}`);
  return response.data;
};

export const updateRetailer = async (id: string, retailerData: { name: string; short_name: string; logo: string; domain: string; enable: boolean }) => {
  const response = await apiClient.put(`/retailer/update/${id}`, retailerData);
  return response.data;
};

export const createRetailer = async (retailerData: { name: string; short_name: string; logo: string; domain: string; enable: boolean; country_id: number }) => {
  const response = await apiClient.post('/retailer/create', retailerData);
  return response.data;
};

export const deleteRetailer = async (id: string) => {
  const response = await apiClient.delete(`/retailer/delete/${id}`);
  return response.data;
};

// Delivery Addresses
export const fetchDeliveryAddresses = async (offset: number, count: number) => {
  const response = await apiClient.get(`/delivery-address/from/${offset}/count/${count}`);
  return response.data;
};

export const fetchDeliveryAddressesCount = async () => {
  const response = await apiClient.get('/delivery-address/total/count');
  if (typeof response.data === 'number') {
    return response.data;
  }
  return response.data.count ?? response.data.total ?? 0;
};

export const fetchDeliveryAddressById = async (id: string) => {
  const response = await apiClient.get(`/delivery-address/${id}`);
  return response.data;
};

export const updateDeliveryAddress = async (id: string, addressData: { user_id: string; address_line_1: string; address_line_2: string; town: string; state_id: number; postcode: string; country_id: number; is_default: boolean }) => {
  const response = await apiClient.put(`/delivery-address/update/${id}`, addressData);
  return response.data;
};

export const createDeliveryAddress = async (addressData: { user_id: string; address_line_1: string; address_line_2: string; town: string; state_id: number; postcode: string; country_id: number; is_default: boolean }) => {
  const response = await apiClient.post('/delivery-address/create', addressData);
  return response.data;
};

export const deleteDeliveryAddress = async (id: string) => {
  const response = await apiClient.delete(`/delivery-address/delete/${id}`);
  return response.data;
};

// Post Reports
export interface PostReport {
  report_id: number;
  post_id: number;
  post_content: string | null;
  post_media: { url: string; type: string }[] | null;
  post_is_active: boolean | null;
  reporter_user_id: string;
  reporter_name: string | null;
  post_owner_id: string | null;
  post_owner_name: string | null;
  reason: 'spam' | 'inappropriate' | 'harassment' | 'other';
  description: string | null;
  status: 'pending' | 'reviewed' | 'dismissed' | 'action_taken';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string | null;
}

export interface PostReportCounts {
  pending: number;
  reviewed: number;
  dismissed: number;
  action_taken: number;
  total: number;
}

export const fetchPostReports = async (adminEmail: string, status?: string, offset: number = 0, limit: number = 20): Promise<PostReport[]> => {
  const params: Record<string, string | number> = { admin_email: adminEmail, skip: offset, limit };
  if (status) {
    params.status = status;
  }
  const response = await apiClient.get('/posts/reports', { params });
  return response.data;
};

export const fetchPostReportCounts = async (adminEmail: string): Promise<PostReportCounts> => {
  const response = await apiClient.get('/posts/reports/count', { params: { admin_email: adminEmail } });
  return response.data;
};

export const fetchPostReportById = async (reportId: number, adminEmail: string): Promise<PostReport> => {
  const response = await apiClient.get(`/posts/reports/${reportId}`, { params: { admin_email: adminEmail } });
  return response.data;
};

export const updatePostReportStatus = async (reportId: number, status: 'pending' | 'reviewed' | 'dismissed' | 'action_taken', adminEmail: string) => {
  const response = await apiClient.put(`/posts/reports/${reportId}`, { status }, { params: { admin_email: adminEmail } });
  return response.data;
};

// ──────────────────────────────────────────────────────────
// Create Post (admin)
// ──────────────────────────────────────────────────────────

export interface UserSearchResult {
  user_id: string;
  name: string;
  email: string | null;
  avater: string | null;
}

export const searchUsers = async (query: string): Promise<UserSearchResult[]> => {
  const response = await apiClient.get('/user/search', { params: { q: query } });
  return response.data;
};

export const uploadPostMedia = async (userId: string, files: File[]): Promise<string[]> => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const response = await apiClient.post(
    `/posts/upload-media/${encodeURIComponent(userId)}`,
    formData,
    { headers: { 'Content-Type': undefined } },
  );
  return response.data.urls;
};

export const createPost = async (userId: string, content: string, mediaUrls: string[]): Promise<{ post_id: number }> => {
  const response = await apiClient.post(`/posts/create/${encodeURIComponent(userId)}`, {
    content,
    image_url: mediaUrls[0] || null,
    media_urls: mediaUrls,
  });
  return response.data;
};

// Support Tickets
export interface SupportTicket {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'open' | 'resolved';
  reply: string | null;
  replied_by: string | null;
  replied_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const fetchSupportTickets = async (): Promise<SupportTicket[]> => {
  console.log('📡 API: Fetching support tickets from /support/tickets');
  const response = await apiClient.get('/support/tickets');
  console.log('📡 API: Response received:', response.data);
  return response.data.tickets || [];
};

export const replySupportTicket = async (ticketId: string, reply: string, repliedBy: string) => {
  const response = await apiClient.put(`/support/tickets/${ticketId}/reply`, {
    reply,
    replied_by: repliedBy
  });
  return response.data;
};

// Search Analytics
export interface SearchOverview {
  total_searches: number;
  total_searches_change: number;
  unique_users: number;
  unique_users_change: number;
  no_result_rate: number;
  no_result_rate_change: number;
  unique_queries: number;
  zero_result_count: number;
  barcode_searches: number;
  text_searches: number;
  trending: { query: string; search_count: number }[];
  period: { from: string; to: string };
}

export interface TopQuery {
  rank: number;
  query: string;
  search_count: number;
  unique_users: number;
  avg_results: number;
  search_type?: string;
}

export interface TopQueriesResponse {
  total: number;
  offset: number;
  limit: number;
  queries: TopQuery[];
}

export interface NoResultQuery {
  query: string;
  search_count: number;
  last_searched: string | null;
  country_count: number;
}

export interface NoResultsResponse {
  total: number;
  offset: number;
  limit: number;
  queries: NoResultQuery[];
}

export interface TrendDataPoint {
  date: string;
  search_count: number;
  unique_users: number;
  zero_results: number;
}

export interface CountryBreakdown {
  country_id: number;
  country_name: string;
  total_searches: number;
  unique_users: number;
  percentage: number;
  top_query: string | null;
}

export const fetchSearchOverview = async (
  countryId?: number,
  dateFrom?: string,
  dateTo?: string,
  searchType?: string,
): Promise<SearchOverview> => {
  const params: Record<string, string | number> = {};
  if (countryId) params.country_id = countryId;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (searchType) params.search_type = searchType;
  const response = await apiClient.get('/analytics/search/overview', { params });
  return response.data;
};

export const fetchTopQueries = async (filters: {
  countryId?: number;
  dateFrom?: string;
  dateTo?: string;
  searchType?: string;
  minSearches?: number;
  offset?: number;
  limit?: number;
}): Promise<TopQueriesResponse> => {
  const params: Record<string, string | number> = {};
  if (filters.countryId) params.country_id = filters.countryId;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.searchType) params.search_type = filters.searchType;
  if (filters.minSearches) params.min_searches = filters.minSearches;
  if (filters.offset !== undefined) params.offset = filters.offset;
  if (filters.limit) params.limit = filters.limit;
  const response = await apiClient.get('/analytics/search/top-queries', { params });
  return response.data;
};

export const fetchNoResultQueries = async (filters: {
  countryId?: number;
  dateFrom?: string;
  dateTo?: string;
  offset?: number;
  limit?: number;
}): Promise<NoResultsResponse> => {
  const params: Record<string, string | number> = {};
  if (filters.countryId) params.country_id = filters.countryId;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.offset !== undefined) params.offset = filters.offset;
  if (filters.limit) params.limit = filters.limit;
  const response = await apiClient.get('/analytics/search/no-results', { params });
  return response.data;
};

export const fetchSearchTrends = async (filters: {
  countryId?: number;
  dateFrom?: string;
  dateTo?: string;
  groupBy?: 'day' | 'week' | 'month';
}): Promise<{ group_by: string; data: TrendDataPoint[] }> => {
  const params: Record<string, string | number> = {};
  if (filters.countryId) params.country_id = filters.countryId;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.groupBy) params.group_by = filters.groupBy;
  const response = await apiClient.get('/analytics/search/trends', { params });
  return response.data;
};

export const fetchZeroResultsByCountry = async (filters: {
  groupBy?: 'day' | 'week' | 'month';
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ group_by: string; countries: string[]; data: Record<string, any>[] }> => {
  const params: Record<string, string> = {};
  if (filters.groupBy) params.group_by = filters.groupBy;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  const response = await apiClient.get('/analytics/search/zero-results-by-country', { params });
  return response.data;
};

export const fetchSearchByCountry = async (
  dateFrom?: string,
  dateTo?: string
): Promise<{ countries: CountryBreakdown[] }> => {
  const params: Record<string, string> = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const response = await apiClient.get('/analytics/search/by-country', { params });
  return response.data;
};

export const exportSearchKeywords = async (filters: {
  countryId?: number;
  dateFrom?: string;
  dateTo?: string;
  minSearches?: number;
  resultsFilter?: 'all' | 'no_results' | 'with_results';
}): Promise<void> => {
  const params: Record<string, string | number> = {};
  if (filters.countryId) params.country_id = filters.countryId;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.minSearches) params.min_searches = filters.minSearches;
  if (filters.resultsFilter) params.results_filter = filters.resultsFilter;
  const response = await apiClient.get('/analytics/search/export-keywords', {
    params,
    responseType: 'blob',
  });
  // Trigger browser download
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : `search_keywords_${new Date().toISOString().slice(0, 10)}.csv`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ──────────────────────────────────────────────────────────
// Performance Analytics
// ──────────────────────────────────────────────────────────

export interface SessionDurationStats {
  avg_duration_seconds: number;
  median_duration_seconds: number;
  max_duration_seconds: number;
  min_duration_seconds: number;
  total_sessions: number;
  avg_pages_per_session: number;
}

export const fetchSessionDurationStats = async (
  days: number = 30
): Promise<SessionDurationStats> => {
  const response = await apiClient.get('/analytics/performance/session-duration', {
    params: { days },
  });
  return response.data;
};

// ──────────────────────────────────────────────────────────
// Performance Analytics (Full Page)
// ──────────────────────────────────────────────────────────

export interface PerfOverview {
  avg: number;
  median: number;
  p95: number;
  min: number;
  max: number;
  count: number;
}

export interface PerfByCountry {
  country_id: number | null;
  country_name: string;
  count: number;
  avg_ms: number;
}

export interface PerfTrendPoint {
  date: string;
  avg_ms: number;
  count: number;
}

export interface PerfByPlatform {
  platform: string;
  count: number;
  avg_ms: number;
}

export interface PerfByPage {
  page_name: string;
  count: number;
  avg_ms: number;
}

export const fetchPerfOverview = async (
  days: number = 30,
  pageName?: string,
): Promise<PerfOverview> => {
  const params: Record<string, string | number> = { days };
  if (pageName) params.page_name = pageName;
  const response = await apiClient.get('/analytics/performance/overview', { params });
  return response.data;
};

export const fetchPerfByCountry = async (
  days: number = 30,
  pageName?: string,
): Promise<PerfByCountry[]> => {
  const params: Record<string, string | number> = { days };
  if (pageName) params.page_name = pageName;
  const response = await apiClient.get('/analytics/performance/by-country', { params });
  return response.data;
};

export const fetchPerfTrends = async (
  days: number = 30,
  pageName?: string,
): Promise<PerfTrendPoint[]> => {
  const params: Record<string, string | number> = { days };
  if (pageName) params.page_name = pageName;
  const response = await apiClient.get('/analytics/performance/trends', { params });
  return response.data;
};

export const fetchPerfByPlatform = async (
  days: number = 30,
  pageName?: string,
): Promise<PerfByPlatform[]> => {
  const params: Record<string, string | number> = { days };
  if (pageName) params.page_name = pageName;
  const response = await apiClient.get('/analytics/performance/by-platform', { params });
  return response.data;
};

export const fetchPerfByPage = async (
  days: number = 30,
): Promise<PerfByPage[]> => {
  const response = await apiClient.get('/analytics/performance/by-page', { params: { days } });
  return response.data;
};

// ──────────────────────────────────────────────────────────
// User Registration Stats
// ──────────────────────────────────────────────────────────

export interface RegistrationDataPoint {
  period: string;
  country_name: string;
  country_id: number | null;
  count: number;
}

export interface RegistrationStatsResponse {
  group_by: string;
  countries: string[];
  data: RegistrationDataPoint[];
}

export const fetchUserRegistrationStats = async (
  groupBy: 'daily' | 'weekly' | 'monthly' = 'monthly',
  countryId?: number,
  dateFrom?: string,
  dateTo?: string,
  testAccount?: boolean,
): Promise<RegistrationStatsResponse> => {
  const params: Record<string, string | number | boolean> = { group_by: groupBy };
  if (countryId) params.country_id = countryId;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (testAccount !== undefined) params.test_account = testAccount;
  const response = await apiClient.get('/analytics/users/registrations', { params });
  return response.data;
};

// ──────────────────────────────────────────────────────────
// User Activity Analytics
// ──────────────────────────────────────────────────────────

export interface RecentUser {
  user_id: string;
  name: string;
  email: string | null;
  avatar: string | null;
  platform: string | null;
  pages_browsed: number;
  unique_pages: number;
  session_count: number;
  total_duration_seconds: number;
  avg_load_time_ms: number;
  country: string;
  last_active: string;
}

export interface TimelineEvent {
  page_name: string;
  load_time_ms: number;
  timestamp: string;
  time_on_page_seconds: number;
  platform: string | null;
  app_version: string | null;
}

export interface TimelineSession {
  session_id: string;
  start: string;
  end: string;
  duration_seconds: number;
  event_count: number;
  events: TimelineEvent[];
}

export interface UserTimeline {
  user_id: string;
  sessions: TimelineSession[];
  summary: {
    total_sessions: number;
    total_events: number;
    page_breakdown: { page_name: string; visit_count: number; avg_load_time_ms: number }[];
  };
}

export const fetchRecentUsers = async (
  days: number = 30,
  limit: number = 20,
  testAccount?: boolean,
): Promise<RecentUser[]> => {
  const params: Record<string, number | boolean> = { days, limit };
  if (testAccount !== undefined) params.test_account = testAccount;
  const response = await apiClient.get('/analytics/performance/recent-users', {
    params,
  });
  return response.data;
};

export const fetchUserTimeline = async (
  userId: string,
  days: number = 30,
): Promise<UserTimeline> => {
  const response = await apiClient.get(`/analytics/performance/user-timeline/${encodeURIComponent(userId)}`, {
    params: { days },
  });
  return response.data;
};

// ──────────────────────────────────────────────────────────
// User Bookmarks & Shopping Cart (admin view)
// ──────────────────────────────────────────────────────────

export interface UserBookmarkItem {
  id: number;
  name: string;
  avater: string;
  lowest_price: number;
  is_feature: boolean;
  bookmark_count: number;
  created_at: string | null;
}

export interface UserCartItem {
  shopping_id: number;
  item_id: number;
  itemkit_id: number;
  itemkit_name: string | null;
  item_name: string;
  avater: string;
  price: number;
  regular_price: number;
  is_available: boolean;
  url: string;
  quantity: number;
  retailer: {
    id: number | null;
    name: string | null;
    short_name: string | null;
    logo: string | null;
  };
  created_at: string | null;
  added_by_user_id: string | null;
  added_by_user_name: string | null;
  added_by_user_avatar: string | null;
}

export const fetchUserBookmarks = async (userId: string): Promise<UserBookmarkItem[]> => {
  const response = await apiClient.get(`/bookmark/${encodeURIComponent(userId)}`);
  return response.data;
};

export const fetchUserCart = async (userId: string): Promise<UserCartItem[]> => {
  const response = await apiClient.get(`/shopping/${encodeURIComponent(userId)}`);
  return response.data;
};

export interface UserProductView {
  id: number;
  itemkits_id: number;
  product_name: string;
  product_image: string | null;
  view_count: number;
  device: string | null;
  os: string | null;
  browser: string | null;
  viewed_at: string | null;
}

export const fetchUserProductViews = async (
  userId: string,
  days: number = 30,
): Promise<UserProductView[]> => {
  const response = await apiClient.get(
    `/analytics/performance/user-product-views/${encodeURIComponent(userId)}`,
    { params: { days } },
  );
  return response.data;
};

export interface UserSearchKeyword {
  id: number;
  keyword: string;
  results_count: number;
  country: string | null;
  searched_at: string | null;
}

export const fetchUserSearches = async (
  userId: string,
  days: number = 30,
): Promise<UserSearchKeyword[]> => {
  const response = await apiClient.get(
    `/analytics/performance/user-searches/${encodeURIComponent(userId)}`,
    { params: { days } },
  );
  return response.data;
};

export interface UserBarcodeScan {
  id: number;
  barcode: string;
  results_count: number;
  country: string | null;
  scanned_at: string | null;
}

export const fetchUserBarcodeScans = async (
  userId: string,
  days: number = 30,
): Promise<UserBarcodeScan[]> => {
  const response = await apiClient.get(
    `/analytics/performance/user-barcode-scans/${encodeURIComponent(userId)}`,
    { params: { days } },
  );
  return response.data;
};

// ──────────────────────────────────────────────────────────
// Roles & Permissions
// ──────────────────────────────────────────────────────────

export interface PermissionInfo {
  key: string;
  label: string;
}

export interface RoleData {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: string[];
  user_count: number;
  created_at: string;
  updated_at: string;
}

export const fetchPermissionKeys = async (): Promise<PermissionInfo[]> => {
  const response = await apiClient.get('/roles/permissions');
  return response.data;
};

export const fetchRoles = async (): Promise<RoleData[]> => {
  const response = await apiClient.get('/roles/');
  return response.data;
};

export const fetchRoleById = async (id: number): Promise<RoleData> => {
  const response = await apiClient.get(`/roles/${id}`);
  return response.data;
};

export const createRole = async (data: { name: string; description?: string; permissions: string[] }): Promise<RoleData> => {
  const response = await apiClient.post('/roles/', data);
  return response.data;
};

export const updateRole = async (id: number, data: { name?: string; description?: string; permissions?: string[] }): Promise<RoleData> => {
  const response = await apiClient.put(`/roles/${id}`, data);
  return response.data;
};

export const deleteRole = async (id: number): Promise<void> => {
  await apiClient.delete(`/roles/${id}`);
};

// ──────────────────────────────────────────────────────────
// Super Users Management
// ──────────────────────────────────────────────────────────

export interface SuperUserData {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  enable: boolean;
  role_id: number | null;
  role_name: string | null;
}

export const fetchSuperUsers = async (): Promise<SuperUserData[]> => {
  const response = await apiClient.get('/superuser/list');
  return response.data;
};

export const createSuperUser = async (data: {
  username: string;
  email: string;
  phone?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  password: string;
  role_id: number;
}): Promise<any> => {
  const response = await apiClient.post('/superuser/create', data);
  return response.data;
};

export const updateSuperUser = async (id: number, data: {
  username?: string;
  email?: string;
  phone?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  password?: string;
  enable?: boolean;
  role_id?: number;
}): Promise<any> => {
  const response = await apiClient.put(`/superuser/edit/${id}`, data);
  return response.data;
};

export const deleteSuperUser = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/superuser/delete/${id}`);
  return response.data;
};

// ──────────────────────────────────────────────────────────
// Marketing Messages
// ──────────────────────────────────────────────────────────

export interface MarketingMessage {
  id: number;
  country_id: number;
  title: string;
  message: string;
  icon_name: string | null;
  is_active: boolean;
  priority: number;
  view_count: number;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export const fetchMarketingMessages = async (countryId?: number): Promise<MarketingMessage[]> => {
  let url = '/marketing-messages/list';
  if (countryId !== undefined) {
    url += `?country_id=${countryId}`;
  }
  const response = await apiClient.get(url);
  return response.data;
};

export const createMarketingMessage = async (data: {
  country_id: number;
  title: string;
  message: string;
  icon_name?: string;
  is_active?: boolean;
  priority?: number;
  expires_at?: string | null;
}): Promise<MarketingMessage> => {
  const response = await apiClient.post('/marketing-messages/', data);
  return response.data;
};

export const updateMarketingMessage = async (id: number, data: {
  country_id?: number;
  title?: string;
  message?: string;
  icon_name?: string;
  is_active?: boolean;
  priority?: number;
  expires_at?: string | null;
  clear_expires_at?: boolean;
}): Promise<MarketingMessage> => {
  const response = await apiClient.put(`/marketing-messages/${id}`, data);
  return response.data;
};

export const deleteMarketingMessage = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/marketing-messages/${id}`);
  return response.data;
};

// ── Product Clicks Analytics ──

export interface ProductClickItem {
  id: number;
  user_id: string | null;
  user_name: string;
  product_id: number;
  itemkits_id: number;
  product_name: string;
  retailer_id: number | null;
  retailer_name: string;
  country_name: string;
  url: string;
  platform: string | null;
  app_version: string | null;
  clicked_at: string;
}

export interface ProductClicksOverview {
  total_clicks: number;
  unique_users: number;
  unique_products: number;
  unique_retailers: number;
  top_products: { itemkits_id: number; name: string; clicks: number }[];
  top_retailers: { retailer_id: number; name: string; clicks: number }[];
}

export const fetchProductClicks = async (
  days: number = 30,
  countryId?: number,
  limit: number = 50,
  offset: number = 0,
): Promise<{ total: number; items: ProductClickItem[] }> => {
  const params: any = { days, limit, offset };
  if (countryId !== undefined) params.country_id = countryId;
  const response = await apiClient.get('/analytics/product-clicks', { params });
  return response.data;
};

export const fetchProductClicksOverview = async (
  days: number = 30,
  countryId?: number,
): Promise<ProductClicksOverview> => {
  const params: any = { days };
  if (countryId !== undefined) params.country_id = countryId;
  const response = await apiClient.get('/analytics/product-clicks/overview', { params });
  return response.data;
};

// ─── Fuel Stations (Admin) ───

export const fetchFuelStations = async (
  offset: number = 0,
  limit: number = 20,
  countryId?: number,
  brand?: string,
  verified?: boolean,
  q?: string,
): Promise<{ items: FuelStation[]; total: number }> => {
  const params: any = { offset, limit };
  if (countryId !== undefined) params.country_id = countryId;
  if (brand) params.brand = brand;
  if (verified !== undefined) params.verified = verified;
  if (q) params.q = q;
  const response = await apiClient.get('/fuel-stations/admin/list', { params });
  return response.data;
};

export const fetchFuelStationsCount = async (
  countryId?: number,
  brand?: string,
  verified?: boolean,
): Promise<number> => {
  const params: any = {};
  if (countryId !== undefined) params.country_id = countryId;
  if (brand) params.brand = brand;
  if (verified !== undefined) params.verified = verified;
  const response = await apiClient.get('/fuel-stations/admin/count', { params });
  return response.data.count ?? 0;
};

export const fetchFuelStationBrands = async (countryId?: number): Promise<string[]> => {
  const params: any = {};
  if (countryId !== undefined) params.country_id = countryId;
  const response = await apiClient.get('/fuel-stations/admin/brands', { params });
  return response.data;
};

export const updateFuelStation = async (
  id: number,
  data: Partial<{ name: string; brand: string; address: string; suburb: string; postcode: string; is_verified: boolean; is_active: boolean }>,
): Promise<FuelStation> => {
  const response = await apiClient.put(`/fuel-stations/admin/${id}`, data);
  return response.data;
};

export const bulkVerifyFuelStations = async (stationIds: number[]): Promise<{ verified: number }> => {
  const response = await apiClient.post('/fuel-stations/admin/bulk-verify', stationIds);
  return response.data;
};

export const deleteFuelStation = async (id: number): Promise<{ deleted: boolean }> => {
  const response = await apiClient.delete(`/fuel-stations/admin/${id}`);
  return response.data;
};

// ─── Fuel Price Reports (Admin) ───

export const fetchFuelPriceReports = async (
  offset: number = 0,
  limit: number = 20,
  stationId?: number,
  fuelTypeId?: number,
  userId?: string,
  isValid?: boolean,
): Promise<{ items: FuelPriceReport[]; total: number }> => {
  const params: any = { offset, limit };
  if (stationId !== undefined) params.station_id = stationId;
  if (fuelTypeId !== undefined) params.fuel_type_id = fuelTypeId;
  if (userId) params.user_id = userId;
  if (isValid !== undefined) params.is_valid = isValid;
  const response = await apiClient.get('/fuel-prices/admin/reports', { params });
  return response.data;
};

export const fetchFuelPriceReportsCount = async (
  stationId?: number,
  isValid?: boolean,
): Promise<number> => {
  const params: any = {};
  if (stationId !== undefined) params.station_id = stationId;
  if (isValid !== undefined) params.is_valid = isValid;
  const response = await apiClient.get('/fuel-prices/admin/reports/count', { params });
  return response.data.count ?? 0;
};

export const invalidateFuelPriceReport = async (reportId: number): Promise<void> => {
  await apiClient.put(`/fuel-prices/admin/reports/${reportId}/invalidate`);
};

// ─── Fuel Analytics (Admin) ───

export const fetchFuelAnalytics = async (
  countryId: number = 1,
  days: number = 30,
): Promise<FuelAnalytics> => {
  const params: any = { country_id: countryId, days };
  const response = await apiClient.get('/fuel-prices/admin/analytics', { params });
  return response.data;
};

// ─── Crash Reports (Admin) ───

export interface CrashReportItem {
  id: number;
  user_id: string | null;
  user_name: string | null;
  error: string;
  stack_trace: string | null;
  context: string | null;
  platform: string | null;
  app_version: string | null;
  resolved: boolean;
  timestamp: string | null;
}

export const fetchCrashReports = async (
  days: number = 7,
  resolved?: boolean,
  limit: number = 100,
): Promise<CrashReportItem[]> => {
  const params: any = { days, limit };
  if (resolved !== undefined) params.resolved = resolved;
  const response = await apiClient.get('/analytics/crash-reports', { params });
  return response.data;
};

export const resolveCrashReport = async (reportId: number): Promise<void> => {
  await apiClient.patch(`/analytics/crash-reports/${reportId}/resolve`);
};

// Deliverable Postcodes
export interface DeliverablePostcode {
  id: number;
  postcode: string;
  town: string;
  state: { id: number; name: string };
  country: { id: number; name: string };
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export const fetchDeliverablePostcodesCount = async (): Promise<number> => {
  const response = await apiClient.get('/deliverable-postcodes/total/count');
  return response.data.count;
};

export const fetchDeliverablePostcodes = async (
  offset: number = 0,
  limit: number = 20,
  countryId?: number,
): Promise<DeliverablePostcode[]> => {
  const params: any = { offset, limit };
  if (countryId !== undefined) params.country_id = countryId;
  const response = await apiClient.get('/deliverable-postcodes/list', { params });
  return response.data;
};

export const createDeliverablePostcode = async (data: {
  postcode: string;
  town: string;
  state_id: number;
  country_id: number;
  latitude?: number | null;
  longitude?: number | null;
  is_active?: boolean;
}): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/deliverable-postcodes/create', data);
  return response.data;
};

export const updateDeliverablePostcode = async (
  id: number,
  data: Partial<{
    postcode: string;
    town: string;
    state_id: number;
    country_id: number;
    latitude: number | null;
    longitude: number | null;
    is_active: boolean;
  }>,
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/deliverable-postcodes/update/${id}`, data);
  return response.data;
};

export const toggleDeliverablePostcodeActive = async (
  id: number,
): Promise<{ message: string; is_active: boolean }> => {
  const response = await apiClient.put(`/deliverable-postcodes/${id}/toggle-active`);
  return response.data;
};

export const deleteDeliverablePostcode = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/deliverable-postcodes/delete/${id}`);
  return response.data;
};

// ─── Zones ───

export const fetchZonesCount = async (): Promise<{ count: number }> => {
  const response = await apiClient.get('/zones/total/count');
  return response.data;
};

export const fetchZones = async (offset: number, limit: number): Promise<any[]> => {
  const response = await apiClient.get(`/zones/list?offset=${offset}&limit=${limit}`);
  return response.data;
};

export const fetchZoneById = async (id: number): Promise<any> => {
  const response = await apiClient.get(`/zones/${id}`);
  return response.data;
};

export const createZone = async (data: {
  name: string;
  description?: string;
  country_id: number;
  state_id?: number;
  manager_id?: number;
  delivery_fee?: number;
}): Promise<any> => {
  const response = await apiClient.post('/zones/create', data);
  return response.data;
};

export const updateZone = async (id: number, data: {
  name?: string;
  description?: string;
  country_id?: number;
  state_id?: number;
  manager_id?: number;
  delivery_fee?: number;
  is_active?: boolean;
}): Promise<any> => {
  const response = await apiClient.put(`/zones/update/${id}`, data);
  return response.data;
};

export const deleteZone = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/zones/delete/${id}`);
  return response.data;
};

export const assignPostcodesToZone = async (zoneId: number, postcodeIds: number[]): Promise<any> => {
  const response = await apiClient.post(`/zones/${zoneId}/postcodes`, { postcode_ids: postcodeIds });
  return response.data;
};

export const removePostcodeFromZone = async (zoneId: number, postcodeId: number): Promise<any> => {
  const response = await apiClient.delete(`/zones/${zoneId}/postcodes/${postcodeId}`);
  return response.data;
};

export const fetchUnassignedPostcodes = async (countryId?: number): Promise<any[]> => {
  const params = countryId ? `?country_id=${countryId}` : '';
  const response = await apiClient.get(`/zones/unassigned-postcodes${params}`);
  return response.data;
};

// ─── Drivers ───

export const fetchDriversCount = async (): Promise<{ count: number }> => {
  const response = await apiClient.get('/drivers/total/count');
  return response.data;
};

export const fetchDrivers = async (offset: number, limit: number): Promise<any[]> => {
  const response = await apiClient.get(`/drivers/list?offset=${offset}&limit=${limit}`);
  return response.data;
};

export const fetchDriverById = async (id: number): Promise<any> => {
  const response = await apiClient.get(`/drivers/detail/${id}`);
  return response.data;
};

export const registerDriver = async (data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  vehicle_type?: string;
  vehicle_number?: string;
}): Promise<any> => {
  const response = await apiClient.post('/drivers/auth/register', data);
  return response.data;
};

export const assignDriverToZone = async (driverId: number, zoneId: number): Promise<any> => {
  const response = await apiClient.put(`/drivers/${driverId}/assign-zone/${zoneId}`);
  return response.data;
};

export const toggleDriverActive = async (driverId: number): Promise<any> => {
  const response = await apiClient.put(`/drivers/${driverId}/toggle-active`);
  return response.data;
};

export const deleteDriver = async (driverId: number): Promise<any> => {
  const response = await apiClient.delete(`/drivers/delete/${driverId}`);
  return response.data;
};

// ─── Orders ───

export const fetchOrdersCount = async (zoneId?: number, status?: string): Promise<{ count: number }> => {
  const params = new URLSearchParams();
  if (zoneId) params.append('zone_id', String(zoneId));
  if (status) params.append('status', status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(`/orders/total/count${qs}`);
  return response.data;
};

export const fetchOrders = async (
  offset: number,
  limit: number,
  zoneId?: number,
  status?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<any[]> => {
  const params = new URLSearchParams();
  params.append('offset', String(offset));
  params.append('limit', String(limit));
  if (zoneId) params.append('zone_id', String(zoneId));
  if (status) params.append('status', status);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  const response = await apiClient.get(`/orders/list?${params.toString()}`);
  return response.data;
};

export const fetchOrderById = async (id: number): Promise<any> => {
  const response = await apiClient.get(`/orders/detail/${id}`);
  return response.data;
};

export const confirmOrder = async (id: number): Promise<any> => {
  const response = await apiClient.put(`/orders/${id}/confirm`);
  return response.data;
};

export const assignDriverToOrder = async (orderId: number, driverId: number): Promise<any> => {
  const response = await apiClient.put(`/orders/${orderId}/assign-driver/${driverId}`);
  return response.data;
};

export const adminCancelOrder = async (id: number): Promise<any> => {
  const response = await apiClient.put(`/orders/${id}/admin-cancel`);
  return response.data;
};

export const refundOrder = async (id: number): Promise<any> => {
  const response = await apiClient.put(`/orders/${id}/refund`);
  return response.data;
};

export const refundUnavailableItems = async (id: number): Promise<any> => {
  const response = await apiClient.put(`/orders/${id}/refund-items`);
  return response.data;
};

// ─── Retailer Locations ───

export const fetchRetailerLocationsByZone = async (zoneId: number): Promise<any[]> => {
  const response = await apiClient.get(`/retailer-locations/by-zone/${zoneId}`);
  return response.data;
};

export const createRetailerLocation = async (data: {
  retailer_id: number;
  zone_id: number;
  address: string;
  latitude: number;
  longitude: number;
}): Promise<any> => {
  const response = await apiClient.post('/retailer-locations/create', data);
  return response.data;
};

export const deleteRetailerLocation = async (locationId: number): Promise<any> => {
  const response = await apiClient.delete(`/retailer-locations/delete/${locationId}`);
  return response.data;
};

/* ── Notifications ── */

export const fetchUserFcmTokens = async (userId: string): Promise<{ status: string; user_id: string; tokens: { token: string; platform: string }[] }> => {
  const response = await apiClient.get(`/fcm/tokens/${encodeURIComponent(userId)}`);
  return response.data;
};

export const sendUserNotification = async (userId: string, title: string, body: string): Promise<any> => {
  const response = await apiClient.post('/notifications/send', { user_id: userId, title, body });
  return response.data;
};

export const sendBulkNotification = async (params: {
  title: string; body: string;
  country_id?: number; state_id?: number; postcode?: string;
}): Promise<any> => {
  const response = await apiClient.post('/notifications/send-bulk', params);
  return response.data;
};

export const fetchBulkNotificationCount = async (params: {
  country_id?: number; state_id?: number; postcode?: string;
}): Promise<{ user_count: number; token_count: number }> => {
  const response = await apiClient.get('/notifications/bulk-count', { params });
  return response.data;
};

export const fetchUserPostcodes = async (stateId: number): Promise<string[]> => {
  const response = await apiClient.get('/notifications/user-postcodes', { params: { state_id: stateId } });
  return response.data;
};

// ─── Coupons ───

export const fetchCouponsCount = async (): Promise<{ count: number }> => {
  const response = await apiClient.get('/coupons/total/count');
  return response.data;
};

export const fetchCoupons = async (offset: number, limit: number): Promise<any[]> => {
  const response = await apiClient.get(`/coupons/list?offset=${offset}&limit=${limit}`);
  return response.data;
};

export const fetchCouponById = async (id: number): Promise<any> => {
  const response = await apiClient.get(`/coupons/detail/${id}`);
  return response.data;
};

export const createCoupon = async (data: {
  code: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  expires_at?: string;
  max_uses?: number;
}): Promise<any> => {
  const response = await apiClient.post('/coupons/create', data);
  return response.data;
};

export const updateCoupon = async (id: number, data: {
  code?: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  expires_at?: string;
  max_uses?: number;
  is_active?: boolean;
}): Promise<any> => {
  const response = await apiClient.put(`/coupons/update/${id}`, data);
  return response.data;
};

export const deleteCoupon = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/coupons/delete/${id}`);
  return response.data;
};

// ─── Payment Gateways ────────────────────────────────────────────────

export const fetchPaymentGateways = async (): Promise<any[]> => {
  const response = await apiClient.get('/admin/payment-gateways');
  return response.data;
};

export const fetchCountryPaymentGateways = async (countryId: number): Promise<any[]> => {
  const response = await apiClient.get(`/admin/payment-gateways/country/${countryId}`);
  return response.data;
};

export const toggleCountryGateway = async (
  countryId: number,
  gatewayId: number,
  isActive: boolean,
): Promise<any> => {
  const response = await apiClient.post(
    `/admin/payment-gateways/country/${countryId}/gateway/${gatewayId}/toggle`,
    { is_active: isActive },
  );
  return response.data;
};

export const updateCountryGatewayConfig = async (
  countryId: number,
  gatewayId: number,
  data: { currency?: string; display_order?: number; config?: Record<string, any> },
): Promise<any> => {
  const response = await apiClient.put(
    `/admin/payment-gateways/country/${countryId}/gateway/${gatewayId}/config`,
    data,
  );
  return response.data;
};

export const fetchGatewayCredentials = async (
  countryId: number,
  gatewayId: number,
): Promise<any[]> => {
  const response = await apiClient.get(
    `/admin/payment-gateways/country/${countryId}/gateway/${gatewayId}/credentials`,
  );
  return response.data;
};

export const saveGatewayCredentials = async (
  countryId: number,
  gatewayId: number,
  data: { environment: string; credentials: Record<string, string> },
): Promise<any> => {
  const response = await apiClient.put(
    `/admin/payment-gateways/country/${countryId}/gateway/${gatewayId}/credentials`,
    data,
  );
  return response.data;
};

// ─── Ad Config ───────────────────────────────────────────────────────

export const fetchAdConfig = async (): Promise<any> => {
  const response = await apiClient.get('/ad-config/config');
  return response.data;
};

export const updateAdConfig = async (data: {
  ads_enabled: boolean;
  ad_frequency: number;
  grace_period: number;
  native_ad_interval: number;
}): Promise<any> => {
  const response = await apiClient.put('/ad-config/config', null, { params: data });
  return response.data;
};

// ─── Cache Config ────────────────────────────────────────────────────

export const fetchCacheConfigs = async (): Promise<any> => {
  const response = await apiClient.get('/cache-config/config');
  return response.data;
};

export const updateCacheConfig = async (
  featureKey: string,
  data: { cache_enabled: boolean; cache_ttl_minutes: number }
): Promise<any> => {
  const response = await apiClient.put(`/cache-config/config/${featureKey}`, null, { params: data });
  return response.data;
};

export const clearCache = async (featureKey: string): Promise<any> => {
  const response = await apiClient.post(`/cache-config/clear/${featureKey}`);
  return response.data;
};

// ─── Feedback Campaigns ─────────────────────────────────────────────

export type FeedbackQuestionType =
  | 'rating'
  | 'nps'
  | 'single_choice'
  | 'multi_choice'
  | 'short_text'
  | 'long_text'
  | 'yes_no';

export interface FeedbackQuestion {
  id?: number;
  campaign_id?: number;
  order_index: number;
  type: FeedbackQuestionType;
  text: string;
  required: boolean;
  options?: { value: string; label: string }[] | null;
  config?: Record<string, any> | null;
}

export interface FeedbackCampaign {
  id: number;
  title: string;
  description: string | null;
  country_id: number | null;
  platform: 'android' | 'ios' | 'both';
  target_sample_size: number;
  starts_at: string;
  ends_at: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  min_app_version: number | null;
  min_account_age_days: number;
  created_at: string;
  updated_at: string;
  questions?: FeedbackQuestion[];
  shown_count?: number;
  submitted_count?: number;
}

export interface FeedbackCampaignInput {
  title: string;
  description?: string | null;
  country_id?: number | null;
  platform: 'android' | 'ios' | 'both';
  target_sample_size: number;
  starts_at: string;
  ends_at: string;
  status: FeedbackCampaign['status'];
  min_app_version?: number | null;
  min_account_age_days: number;
  questions: FeedbackQuestion[];
}

export interface FeedbackCampaignAnalytics {
  campaign: FeedbackCampaign;
  shown_count: number;
  submitted_count: number;
  dismissed_count: number;
  pending_count: number;
  completion_rate: number;
  per_question: Array<{
    question_id: number;
    type: FeedbackQuestionType;
    text: string;
    answer_count: number;
    histogram?: Record<string, number>;
    average?: number | null;
    sample_answers?: string[];
  }>;
}

export const fetchFeedbackCampaigns = async (status?: string): Promise<FeedbackCampaign[]> => {
  const url = status ? `/admin/feedback/campaigns?status=${status}` : '/admin/feedback/campaigns';
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchFeedbackCampaign = async (id: number): Promise<FeedbackCampaign> => {
  const response = await apiClient.get(`/admin/feedback/campaigns/${id}`);
  return response.data;
};

export const createFeedbackCampaign = async (data: FeedbackCampaignInput): Promise<FeedbackCampaign> => {
  const response = await apiClient.post('/admin/feedback/campaigns', data);
  return response.data;
};

export const updateFeedbackCampaign = async (
  id: number,
  data: Partial<Omit<FeedbackCampaignInput, 'questions'>>,
): Promise<FeedbackCampaign> => {
  const response = await apiClient.put(`/admin/feedback/campaigns/${id}`, data);
  return response.data;
};

export const deleteFeedbackCampaign = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/admin/feedback/campaigns/${id}`);
  return response.data;
};

export const addFeedbackQuestion = async (
  campaignId: number,
  question: FeedbackQuestion,
): Promise<FeedbackQuestion> => {
  const response = await apiClient.post(`/admin/feedback/campaigns/${campaignId}/questions`, question);
  return response.data;
};

export const updateFeedbackQuestion = async (
  questionId: number,
  question: FeedbackQuestion,
): Promise<FeedbackQuestion> => {
  const response = await apiClient.put(`/admin/feedback/questions/${questionId}`, question);
  return response.data;
};

export const deleteFeedbackQuestion = async (questionId: number): Promise<any> => {
  const response = await apiClient.delete(`/admin/feedback/questions/${questionId}`);
  return response.data;
};

export const fetchFeedbackCampaignAnalytics = async (
  id: number,
): Promise<FeedbackCampaignAnalytics> => {
  const response = await apiClient.get(`/admin/feedback/campaigns/${id}/analytics`);
  return response.data;
};

export const feedbackCampaignCsvUrl = (id: number): string =>
  `${API_BASE_URL}/admin/feedback/campaigns/${id}/responses.csv`;
